import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  FlaskConical,
  Pencil,
  XCircle,
  ChevronDown,
  ShoppingCart,
  Store,
  FileText,
  PackageMinus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/common";
import { DocLinks } from "@/components/PracticalDocs";
import { InventoryPhoto } from "@/components/InventoryPhoto";
import { api, API, errMsg, fmtDate } from "@/lib/api";

const Row = ({ label, value, testId }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <span className="text-sm font-medium" data-testid={testId}>
      {value || "—"}
    </span>
  </div>
);

const stockQuantity = (quantity, units) =>
  `${quantity} ${Number(quantity) === 1 && units === "units" ? "unit" : units}`;

function Requirements({ reqs }) {
  const [open, setOpen] = useState(false);
  if (!reqs?.length) return null;
  return (
    <div className="rounded-lg border bg-background/60">
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold"
        onClick={() => setOpen(!open)}
        data-testid="draft-requirements-toggle"
      >
        Equipment & materials ({reqs.length}){" "}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul
          className="px-3 pb-3 space-y-1 text-xs"
          data-testid="draft-requirements-list"
        >
          {reqs.map((r, i) => (
            <li key={i} className="flex justify-between gap-3">
              <span>
                {r.item}
                {r.per_group ? (
                  <span className="text-muted-foreground">
                    {" "}
                    ({r.per_group}/group)
                  </span>
                ) : (
                  <span className="text-muted-foreground"> (whole class)</span>
                )}
              </span>
              <span
                className={`font-mono ${r.shortfall ? "text-destructive" : ""}`}
              >
                {r.required} {r.units}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SetsEditor({ draft, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [sets, setSets] = useState(draft.sets || "");
  const save = async () => {
    try {
      const { data } = await api.patch(`/chat/drafts/${draft.id}`, {
        sets: Number(sets),
      });
      onSaved(data);
      setEditing(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!editing) {
    return (
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Sets
        </span>
        <span
          className="text-sm font-medium flex items-center gap-1.5"
          data-testid="draft-sets-value"
        >
          {draft.sets || "—"}
          {draft.suggested_sets && draft.sets !== draft.suggested_sets && (
            <span className="text-xs text-muted-foreground">
              (suggested {draft.suggested_sets})
            </span>
          )}
          <button
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-primary"
            data-testid="draft-sets-edit-button"
          >
            <Pencil className="h-3 w-3" />
          </button>
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-1.5">
      <Input
        type="number"
        min={1}
        max={60}
        value={sets}
        onChange={(e) => setSets(e.target.value)}
        className="h-8 w-20"
        data-testid="draft-sets-input"
      />
      <Button
        size="sm"
        className="h-8"
        onClick={save}
        data-testid="draft-sets-save-button"
      >
        Save
      </Button>
    </div>
  );
}

export function DraftCard({ draft: initial, isLatest, onConfirmed }) {
  const [draft, setDraft] = useState(initial);
  const [risk, setRisk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(isLatest ? "loading" : "superseded");

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  useEffect(() => {
    if (!isLatest) {
      setState("superseded");
      return;
    }
    api
      .get(`/chat/drafts/${initial.id}`)
      .then(({ data }) => {
        if (data.status === "confirmed") setState("confirmed");
        else if (data.status !== "draft") setState("inactive");
        else {
          setDraft(data);
          setState("active");
        }
      })
      .catch(() => setState("inactive"));
  }, [initial.id, initial.version, isLatest]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/drafts/${draft.id}/confirm`, {
        version: draft.version,
        risk_confirmed: risk,
      });
      setState("confirmed");
      onConfirmed?.(data.booking);
      toast.success(`Booking ${data.booking.reference} saved`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const l = draft.lesson || {};
  const active = state === "active";
  const amending = !!draft.amend_reference;
  return (
    <div
      className={`rounded-xl border p-4 space-y-3 transition-opacity ${active ? "border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20" : "opacity-60 bg-muted/30"}`}
      data-testid="booking-draft-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0">
            <FlaskConical className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {state === "confirmed"
                ? "Booking summary — saved"
                : state === "superseded"
                  ? "Earlier version"
                  : amending
                    ? `Amending ${draft.amend_reference}`
                    : "Booking summary — not yet saved"}
            </div>
            <div
              className="font-semibold leading-snug"
              data-testid="draft-practical-name"
            >
              {draft.practical?.name || "Practical not chosen yet"}
            </div>
          </div>
        </div>
        {state === "superseded" && (
          <span className="text-xs text-muted-foreground">Updated below</span>
        )}
        {state === "confirmed" && (
          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Saved
          </span>
        )}
        {state === "inactive" && (
          <span className="text-xs text-muted-foreground">
            No longer active
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Row label="Teacher" value={draft.teacher_name} />
        <Row
          label="Class"
          value={l.class_name ? `${l.class_name} (${l.pupils} pupils)` : null}
          testId="draft-class"
        />
        <Row
          label="Date"
          value={l.date ? `${fmtDate(l.date)} · Week ${l.week}` : null}
          testId="draft-date"
        />
        <Row
          label="Period"
          value={l.period ? `P${l.period} ${l.start}–${l.end}` : null}
          testId="draft-period"
        />
        <Row label="Laboratory" value={l.lab} testId="draft-lab" />
        {active ? (
          <SetsEditor draft={draft} onSaved={setDraft} />
        ) : (
          <Row label="Sets" value={draft.sets} testId="draft-sets-value" />
        )}
      </div>
      {draft.practical?.prep_notes && (
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Prep notes:</span>{" "}
          {draft.practical.prep_notes}
        </p>
      )}
      {draft.notes && (
        <p className="text-xs">
          <span className="font-semibold">Notes for technician:</span>{" "}
          {draft.notes}
        </p>
      )}
      <Requirements reqs={draft.requirements} />
      {draft.documents?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Attached documents
          </div>
          <DocLinks docs={draft.documents} testId={`draft-docs-${draft.id}`} />
        </div>
      )}
      {draft.warnings?.length > 0 && (
        <div className="space-y-1" data-testid="draft-warnings">
          {draft.warnings.map((w, i) => (
            <p
              key={i}
              className="text-xs text-amber-700 dark:text-amber-400 flex gap-1.5"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
              {w}
            </p>
          ))}
        </div>
      )}
      {draft.missing?.length > 0 && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="draft-missing"
        >
          Still needed: {draft.missing.join(", ")}
        </p>
      )}
      {active && !draft.missing?.length && (
        <div className="space-y-3 pt-1 border-t">
          <label className="flex items-start gap-2.5 pt-3 cursor-pointer text-sm">
            <Checkbox
              checked={risk}
              onCheckedChange={(v) => setRisk(!!v)}
              className="mt-0.5"
              data-testid="booking-card-risk-checkbox"
            />
            <span>
              {draft.risk_text}
              {draft.practical?.risk_assessment_ref && (
                <span className="block text-xs text-muted-foreground mt-0.5">
                  School reference: {draft.practical.risk_assessment_ref}
                </span>
              )}
            </span>
          </label>
          <Button
            onClick={confirm}
            disabled={!risk || busy}
            className="w-full sm:w-auto"
            data-testid="booking-card-confirm-button"
          >
            {busy
              ? "Saving…"
              : amending
                ? "Confirm amendment"
                : "Confirm booking"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ChoicesCard({ card, onPick, disabled }) {
  return (
    <div className="space-y-2" data-testid="practical-choices-card">
      <div className="text-xs font-medium text-muted-foreground">
        {card.title}
      </div>
      <div className="flex flex-wrap gap-2">
        {card.options.map((o) => (
          <button
            key={o.id}
            disabled={disabled}
            onClick={() => onPick(`Use "${o.label}".`)}
            data-testid={`practical-choice-chip-${o.id}`}
            className="text-left rounded-full border px-3.5 py-1.5 text-sm hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
          >
            {o.label}{" "}
            <span className="text-xs text-muted-foreground ml-1">{o.meta}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CancelCard({ booking, conversationId, onDone }) {
  const [state, setState] = useState("idle");
  const go = async () => {
    setState("busy");
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`, {
        reason: "Cancelled via Ask Jonny",
        conversation_id: conversationId,
      });
      setState("done");
      onDone?.(data);
      toast.success(`${data.reference} cancelled`);
    } catch (e) {
      toast.error(errMsg(e));
      setState("idle");
    }
  };
  return (
    <div
      className="rounded-xl border border-rose-300/60 bg-rose-50/50 dark:bg-rose-950/20 p-4 space-y-3"
      data-testid="booking-cancel-card"
    >
      <div className="text-sm">
        <span className="font-semibold">Cancel {booking.reference}?</span>{" "}
        {booking.practical} — {booking.class}, {fmtDate(booking.date)} P
        {booking.period}, {booking.lab}
      </div>
      {state === "done" ? (
        <p
          className="text-sm text-rose-700 dark:text-rose-300 font-medium"
          data-testid="booking-cancel-done"
        >
          Cancelled.
        </p>
      ) : (
        <Button
          variant="destructive"
          size="sm"
          onClick={go}
          disabled={state === "busy"}
          data-testid="booking-cancel-confirm-button"
        >
          <XCircle className="h-4 w-4 mr-1.5" />
          {state === "busy" ? "Cancelling…" : "Confirm cancellation"}
        </Button>
      )}
    </div>
  );
}

export function SourcesCard({ documents }) {
  return (
    <div
      className="rounded-xl border bg-card px-4 py-3"
      data-testid="sources-card"
    >
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
        Sources from your school documents
      </div>
      <div className="flex flex-wrap gap-2">
        {documents.map((d) => (
          <a
            key={d.id}
            href={`${API}/documents/${d.id}/file`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs hover:border-primary/60"
            data-testid={`source-link-${d.id}`}
          >
            <FileText className="h-3.5 w-3.5 text-primary" />
            {d.title}
            <span className="text-muted-foreground">· {d.category}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export function InventoryResultsCard({ items }) {
  return (
    <div className="space-y-2" data-testid="inventory-results-card">
      {items.map((item) => (
        <Link
          key={item.id}
          className="flex items-center gap-3 rounded-lg border bg-card p-3 transition-colors hover:border-primary/60"
          to="/inventory"
          data-testid={`inventory-result-card-${item.id}`}
        >
          <InventoryPhoto item={item} compact />
          <div className="min-w-0 flex-1">
            <div className="font-semibold" data-testid={`inventory-result-name-${item.id}`}>
              {item.name}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {[item.quantity, item.units, item.location].filter(Boolean).join(" · ")}
            </div>
          </div>
          {item.enough_for_one_each !== undefined && (
            <span
              className={
                item.enough_for_one_each ? "text-xs text-primary" : "text-xs text-destructive"
              }
              data-testid={`inventory-result-stock-${item.id}`}
            >
              {item.enough_for_one_each ? "Available" : "Check stock"}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
}

export function SupplierCard({ entry, verb }) {
  const [removed, setRemoved] = useState(false);
  const undo = async () => {
    try {
      await api.delete(`/suppliers/${entry.id}`);
      setRemoved(true);
      toast.success("Entry removed from the Supplier finder");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div
      className="rounded-xl border border-emerald-500/40 bg-card p-4 flex items-start gap-3"
      data-testid="supplier-chat-card"
    >
      <Store className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {removed
            ? "Removed from Supplier finder"
            : `Saved to Supplier finder${verb === "updated" ? " (updated)" : ""}`}
        </div>
        <div
          className={`font-semibold ${removed ? "line-through text-muted-foreground" : ""}`}
          data-testid="supplier-chat-card-name"
        >
          {entry.item_name} · {entry.supplier_name}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 break-all">
          {[entry.where_to_buy, entry.phone, entry.email]
            .filter(Boolean)
            .join(" · ") || "No contact details recorded"}
        </div>
      </div>
      {!removed && (
        <div className="flex gap-1.5">
          {verb === "added" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={undo}
              data-testid="supplier-chat-card-undo"
            >
              Undo
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            asChild
            data-testid="supplier-chat-card-open"
          >
            <Link to="/suppliers">Open</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

export function ToBuyCard({ item, verb }) {
  const [removed, setRemoved] = useState(false);
  const undo = async () => {
    try {
      await api.delete(`/tobuy/${item.id}`);
      setRemoved(true);
      toast.success(`${item.name} removed from the To buy list`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div
      className="rounded-xl border border-emerald-500/40 bg-card p-4 flex items-start gap-3"
      data-testid="tobuy-chat-card"
    >
      <ShoppingCart className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {removed
            ? "Removed from To buy list"
            : `Saved to To buy list${verb === "updated" ? " (updated)" : ""}`}
        </div>
        <div
          className={`font-semibold ${removed ? "line-through text-muted-foreground" : ""}`}
          data-testid="tobuy-chat-card-name"
        >
          {item.quantity} {item.units} · {item.name}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {item.priority} priority · {item.status}
          {item.supplier ? ` · ${item.supplier}` : ""}
        </div>
      </div>
      {!removed && (
        <div className="flex gap-1.5">
          {verb === "added" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={undo}
              data-testid="tobuy-chat-card-undo"
            >
              Undo
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            asChild
            data-testid="tobuy-chat-card-open"
          >
            <Link to="/tobuy">Open list</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

export function StockChangeCard({ draft: initial }) {
  const [draft, setDraft] = useState(initial);
  const initialRef = useRef(initial);
  if (initialRef.current.id !== initial.id) initialRef.current = initial;
  const stateFor = (value) =>
    value.status === "pending" ? "active" : value.status;
  const [state, setState] = useState(() => stateFor(initial));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const source = initialRef.current;
    setDraft(source);
    setState(stateFor(source));
    api
      .get(`/stock-changes/drafts/${source.id}`)
      .then(({ data }) => {
        if (!active) return;
        setDraft(data);
        setState(stateFor(data));
      })
      .catch((error) => {
        if (!active) return;
        if ([403, 404].includes(error?.response?.status)) {
          setState("inactive");
        } else {
          setState(stateFor(source));
        }
      });
    return () => {
      active = false;
    };
  }, [initial.id]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/stock-changes/drafts/${draft.id}/confirm`,
      );
      setDraft(data.draft);
      setState("confirmed");
      toast.success("Inventory updated");
    } catch (error) {
      toast.error(errMsg(error));
      if ([404, 409].includes(error?.response?.status)) setState("inactive");
    } finally {
      setBusy(false);
    }
  };

  const active = state === "active";
  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${
        active
          ? "border-amber-400/70 bg-amber-50/50 dark:bg-amber-950/20"
          : "bg-muted/30 opacity-70"
      }`}
      data-testid="stock-change-card"
    >
      <div className="flex items-start gap-2.5">
        <div
          className={
            "grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-500/15 " +
            "text-amber-700 dark:text-amber-300"
          }
        >
          <PackageMinus className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {state === "confirmed"
              ? "Stock change saved"
              : active
                ? "Stock change — review before saving"
                : "Stock change unavailable"}
          </div>
          <div className="font-semibold" data-testid="stock-change-item-name">
            {draft.inventory_name}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Row
          label="Current stock"
          value={stockQuantity(draft.previous_quantity, draft.units)}
          testId="stock-change-current-quantity"
        />
        <Row
          label="Remove"
          value={stockQuantity(draft.reduction, draft.units)}
          testId="stock-change-reduction-quantity"
        />
        <Row
          label="New stock"
          value={stockQuantity(draft.new_quantity, draft.units)}
          testId="stock-change-new-quantity"
        />
      </div>
      <p className="text-sm" data-testid="stock-change-reason">
        <span className="font-semibold">Reason:</span> {draft.reason}
      </p>
      {state === "confirmed" ? (
        <p
          className={
            "flex items-center gap-1.5 text-sm font-medium text-emerald-700 " +
            "dark:text-emerald-300"
          }
          data-testid="stock-change-saved"
        >
          <CheckCircle2 className="h-4 w-4" />
          Inventory updated and logged.
        </p>
      ) : (
        active && (
          <Button
            onClick={confirm}
            disabled={busy}
            data-testid="stock-change-confirm-button"
          >
            {busy ? "Saving…" : "Confirm change"}
          </Button>
        )
      )}
    </div>
  );
}

export function StockChangeConfirmedCard({ log }) {
  return (
    <div
      className={
        "flex items-start gap-3 rounded-xl border border-emerald-500/50 bg-emerald-50 " +
        "p-4 dark:bg-emerald-950/30"
      }
      data-testid="stock-change-confirmed-card"
    >
      <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
      <div>
        <div className="text-sm font-semibold">Inventory updated</div>
        <div
          className="text-xs text-muted-foreground"
          data-testid="stock-change-confirmed-details"
        >
          {log.inventory_name}: {log.previous_quantity} to {log.new_quantity}{" "}
          {log.units} · {log.reason}
        </div>
      </div>
    </div>
  );
}

export function ConfirmedCard({ booking, cancelled }) {
  return (
    <div
      className={`rounded-xl border p-4 flex items-start gap-3 ${cancelled ? "border-rose-300/60" : "border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30"}`}
      data-testid={
        cancelled ? "booking-cancelled-card" : "booking-confirmed-card"
      }
    >
      {cancelled ? (
        <XCircle className="h-5 w-5 text-rose-600 mt-0.5" />
      ) : (
        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
      )}
      <div className="space-y-1">
        <div className="text-sm font-semibold">
          {cancelled
            ? "Booking cancelled"
            : booking.verb === "amended"
              ? "Booking amended"
              : "Booking saved"}{" "}
          ·{" "}
          <span className="font-mono" data-testid="booking-reference">
            {booking.reference}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {booking.practical} — {booking.class}, {fmtDate(booking.date)} (Week{" "}
          {booking.week}) P{booking.period}, {booking.lab} · {booking.sets} sets
        </div>
        <StatusBadge
          status={booking.status}
          testId="booking-confirmed-status"
        />
      </div>
    </div>
  );
}
